import { useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Grid,
    Input
} from "@mui/material"
import {
    GridToolbarContainer,
    GridToolbarFilterButton,
    GridToolbarDensitySelector,
    GridToolbarExport
} from "@mui/x-data-grid"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import { ButtonStyles, DialogStyles } from "../styles/MaintenanceStyles"
import {
    createControlMethod,
    getControlMethods,
    deleteControlMethod,
    updateControlMethod,
    updateselected,
    setAlertDialog,
    setAlertText
} from "../../../actions/maintenanceActions"
import axios from "../../../auth/axiosAuth"
import { backendURL } from "../../../config"

function ControlMethodToolBar() {
    const dispatch = useDispatch()

    // Store Variables
    const maintenance = useSelector((state) => state.maintenance)
    const auth = useSelector((state) => state.auth)
    const permissions = useSelector((state) => state.auth.permissions)

    // Add Dialog State
    const [addDialogValues, setAddDialogValues] = useState({
        controlMethod: ""
    })
    const [addDialogOpen, setAddDialogOpen] = useState(false)

    //edit dialog state
    const [editDialogOpen, setEditDialogOpen] = useState(false)
    const [editDialogValues, setEditDialogValues] = useState({
        controlMethod: ""
    })

    // Delete Dialog State
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

    // Alert Handlers
    const handleAlertDialogClose = (event, reason) => {
        if (reason === "clickaway") {
            return
        }
        dispatch(setAlertDialog(false))
    }

    // Add Dialog Handlers
    const handleAddDialogOpen = () => {
        clearAddDialogValues()
        setAddDialogOpen(true)
    }

    const handleAddDialogClose = () => {
        clearAddDialogValues()
        setAddDialogOpen(false)
        handleAlertDialogClose(null, null)
    }

    const clearAddDialogValues = () => {
        setAddDialogValues({ controlMethod: "" })
    }

    const handleConfirmAdd = async () => {
        await dispatch(getControlMethods())
        if (addDialogValues.controlMethod !== "") {
            if (
                maintenance.controlMethods?.filter(
                    (controlMethod) =>
                        controlMethod.ControlMethod.toLowerCase() ===
                        addDialogValues.controlMethod.toLowerCase()
                ).length > 0
            ) {
                dispatch(setAlertText("This control method already exists"))
                dispatch(setAlertDialog(true))
            } else {
                try {
                    addDialogValues.controlMethod =
                        addDialogValues.controlMethod.trim()
                    const newControlMethod = {
                        ControlMethod: addDialogValues.controlMethod,
                        AuditLog: {
                            Action: "Create Control Method",
                            OldValue: "",
                            NewValue: addDialogValues.controlMethod,
                            AssociateOID: auth.userData.AssociateOID
                        }
                    }
                    await dispatch(createControlMethod(newControlMethod))
                    await dispatch(getControlMethods())
                    setAddDialogOpen(false)
                    clearAddDialogValues()
                } catch (error) {
                    console.log(error)
                    clearAddDialogValues()
                }
            }
        } else {
            dispatch(
                setAlertText("Control Method Name field must be filled out")
            )
            dispatch(setAlertDialog(true))
        }
    }

    const handleUpdateAddDialogValues = (e) => {
        const name = e.target.name
        const value = e.target.value

        setAddDialogValues({
            ...addDialogValues,
            [name]: value
        })
    }

    // Delete Dialog Handlers
    const handleDeleteDialogOpen = async () => {
        const res = await axios.get(
            `${backendURL}/controlmethods/check/${maintenance.selected.ControlMethodId}`
        )
        if (res.data.length > 0) {
            dispatch(
                setAlertText(
                    "This control method is being used and cannot be deleted"
                )
            )
            dispatch(setAlertDialog(true))
            return
        }
        if (maintenance.selected.ControlMethodId) {
            setAddDialogValues({
                controlMethod: maintenance.selected.ControlMethod
            })
            setDeleteDialogOpen(true)
        }
    }

    const handleDeleteDialogClose = () => {
        clearAddDialogValues()
        setDeleteDialogOpen(false)
    }

    const handleConfirmDelete = async () => {
        try {
            const deleteMethod = {
                ControlMethodId: maintenance.selected.ControlMethodId,
                AuditLog: {
                    Action: "Delete Control Method",
                    OldValue: maintenance.selected.ControlMethod,
                    NewValue: "",
                    AssociateOID: auth.userData.AssociateOID
                }
            }
            await dispatch(deleteControlMethod(deleteMethod))
            await dispatch(getControlMethods())
            dispatch(updateselected([]))
            setDeleteDialogOpen(false)
        } catch (error) {
            await dispatch(getControlMethods())
            dispatch(updateselected([]))
            setDeleteDialogOpen(false)
        }
    }

    //Edit Dialog Handlers
    const handleEditDialogOpen = async () => {
        const res = await axios.get(
            `${backendURL}/controlmethods/check/${maintenance.selected.ControlMethodId}`
        )
        if (res.data.length > 0) {
            dispatch(
                setAlertText(
                    "This control method is being used and cannot be updated"
                )
            )
            dispatch(setAlertDialog(true))
            return
        }
        if (maintenance.selected.ControlMethodId) {
            setEditDialogValues({
                controlMethod: maintenance.selected.ControlMethod
            })
            setEditDialogOpen(true)
        }
    }

    const handleEditDialogClose = () => {
        clearAddDialogValues()
        setEditDialogOpen(false)
        handleAlertDialogClose(null, null)
    }

    const handleConfirmEdit = async () => {
        if (editDialogValues.controlMethod !== "") {
            if (
                maintenance.controlMethods?.filter(
                    (controlMethod) =>
                        controlMethod.ControlMethod.toLowerCase() ===
                        editDialogValues.controlMethod.toLowerCase()
                ).length > 0
            ) {
                dispatch(setAlertText("This control method already exists"))
                dispatch(setAlertDialog(true))
            } else {
                try {
                    editDialogValues.controlMethod =
                        editDialogValues.controlMethod.trim()
                    const updatedControlMethod = {
                        ControlMethodId: maintenance.selected.ControlMethodId,
                        ControlMethod: editDialogValues.controlMethod
                    }
                    const newControlMethod = {
                        ControlMethod: updatedControlMethod,
                        AuditLog: {
                            ControlMethodId:
                                maintenance.selected.ControlMethodId,
                            Action: "Update Control Method",
                            OldValue: maintenance.selected.ControlMethod,
                            NewValue: editDialogValues.controlMethod,
                            AssociateOID: auth.userData.AssociateOID
                        }
                    }
                    await dispatch(updateControlMethod(newControlMethod))
                    dispatch(updateselected([updatedControlMethod]))
                    await dispatch(getControlMethods())
                    setEditDialogOpen(false)
                    clearAddDialogValues()
                } catch (error) {
                    console.log(error)
                    clearAddDialogValues()
                }
            }
        } else {
            dispatch(
                setAlertText("Control Method Name field must be filled out")
            )
            dispatch(setAlertDialog(true))
        }
    }

    const handleUpdateEditDialogValues = (e) => {
        const name = e.target.name
        const value = e.target.value

        setEditDialogValues({
            ...editDialogValues,
            [name]: value
        })
    }

    return (
        <Grid>
            {/*ToolBar*/}
            <GridToolbarContainer sx={ButtonStyles}>
                <GridToolbarFilterButton sx={ButtonStyles} />
                <GridToolbarDensitySelector sx={ButtonStyles} />
                <GridToolbarExport sx={ButtonStyles} />
                {permissions.EditMaintenance && (
                    <>
                        <Button
                            startIcon={<AddIcon />}
                            onClick={handleAddDialogOpen}
                            sx={ButtonStyles}
                            disabled={maintenance.isBeingEdited}
                        >
                            Add
                        </Button>
                        {maintenance.selected.ControlMethodId && (
                            <>
                                <Button
                                    startIcon={<EditIcon />}
                                    onClick={handleEditDialogOpen}
                                    sx={ButtonStyles}
                                    disabled={maintenance.isBeingEdited}
                                >
                                    Edit
                                </Button>
                                <Button
                                    startIcon={<DeleteIcon />}
                                    onClick={handleDeleteDialogOpen}
                                    sx={ButtonStyles}
                                    disabled={maintenance.isBeingEdited}
                                >
                                    Delete
                                </Button>
                            </>
                        )}
                    </>
                )}
            </GridToolbarContainer>

            {/*Add control Method Dialog*/}
            <Dialog
                open={addDialogOpen}
                onClose={handleAddDialogClose}
                sx={DialogStyles}
            >
                <DialogTitle>Add Control Method</DialogTitle>
                <DialogContent dividers>
                    <DialogContentText sx={DialogStyles}>
                        Please enter the control method you would like to add
                    </DialogContentText>
                    <Input
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Control Method Name"
                        onChange={handleUpdateAddDialogValues}
                        name="controlMethod"
                        sx={DialogStyles}
                        value={addDialogValues.controlMethod}
                        fullWidth
                        variant="standard"
                        placeholder="Control Method Name"
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleConfirmAdd}
                        sx={DialogStyles}
                    >
                        Add
                    </Button>
                    <Button
                        onClick={handleAddDialogClose}
                        sx={DialogStyles}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/*Delete Control Method Dialog*/}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteDialogClose}
                sx={DialogStyles}
            >
                <DialogTitle>Delete Control Method</DialogTitle>
                <DialogContent dividers>
                    <DialogContentText sx={DialogStyles}>
                        Are you sure you would like to delete{" "}
                        {addDialogValues.controlMethod}?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleConfirmDelete}
                        sx={DialogStyles}
                    >
                        Yes
                    </Button>
                    <Button
                        onClick={handleDeleteDialogClose}
                        sx={DialogStyles}
                    >
                        No
                    </Button>
                </DialogActions>
            </Dialog>

            {/*Edit control Method Dialog*/}
            <Dialog
                open={editDialogOpen}
                onClose={handleEditDialogClose}
                sx={DialogStyles}
            >
                <DialogTitle>Edit Control Method</DialogTitle>
                <DialogContent dividers>
                    <DialogContentText sx={DialogStyles}>
                        What would you like to change the control method to?
                    </DialogContentText>
                    <Input
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Control Method Name"
                        onChange={handleUpdateEditDialogValues}
                        name="controlMethod"
                        sx={DialogStyles}
                        value={editDialogValues.controlMethod}
                        fullWidth
                        variant="standard"
                        placeholder="Control Method Name"
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleConfirmEdit}
                        sx={DialogStyles}
                    >
                        Confirm
                    </Button>
                    <Button
                        onClick={handleEditDialogClose}
                        sx={DialogStyles}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    )
}

export default ControlMethodToolBar
